import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useState } from "react";
import * as Yup from "yup";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

const DataScansApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [scan, setScan] = useState<any | null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [dynamicLabels, setDynamicLabels] = useState({ projectId: 'Project ID' });

    React.useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(
                    `query GetDataScan($id: String!) {
                        getDataScan(id: $id) {
                            id name status reasonForRejection cloudProvider
                            details { dataScans { scanType projectId location description schedule targetProjectId targetTableId targetDatasetId exportProjectId exportDatasetId exportTableId rules { column dimension } excludeFields samplingPercent } }
                            solutionOwner { id name }
                        }
                    }`, { id }
                );
                if (data.getDataScan) setScan(data.getDataScan);
            } catch (error) {
                console.log("Error fetching scan details:", error);
            }
        };
        fetchData();
    }, [id]);

    React.useEffect(() => {
        if (scan?.cloudProvider) {
            switch (scan.cloudProvider) {
                case 'aws': setDynamicLabels({ projectId: 'Account ID' }); break;
                case 'azure': setDynamicLabels({ projectId: 'Subscription ID' }); break;
                default: setDynamicLabels({ projectId: 'Project ID' }); break;
            }
        }
    }, [scan]);

    const statusDisplay = scan?.status ? scan.status : "Status Unknown";
    const statusBgColor = scan?.status === "active" || scan?.status === "approved" ? "#12B76A10" : scan?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = scan?.status === "active" || scan?.status === "approved" ? "/assets/tickcircle.svg" : scan?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";
    const ds = scan?.details?.dataScans || {};

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/datasolutions/requests/data-scans" onClick={(e) => { e.preventDefault(); navigate("/datasolutions/requests/data-scans") }}>
                        Data Scan Requests
                    </Link>
                    <Typography color="text.primary">Data Scan Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/requests/data-scans")} >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Data Scan Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11} md={11} lg={11}>
                    {scan && (
                        <Formik
                            initialValues={{}}
                            onSubmit={async () => {
                                try {
                                    await queryGraphql(`mutation Approve($input: ApproveSolutionInput!) { approveSolutionDataScans(input: $input) { id } }`, { input: { id: scan.id, details: { dataScans: {} } } });
                                    setOpenApproveModal(false);
                                    navigate("/datasolutions/requests/data-scans");
                                } catch (error) { console.error("Error during approval:", error); }
                            }}
                        >
                            {({ }) => (
                                <Form id="approve-scan-form">
                                    <Box my={2}>
                                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                                <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{scan.name}</Typography>
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${scan.solutionOwner?.name}`}</Typography>
                                                </Box>
                                                <Box textAlign="right">
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
                                                        <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{ textTransform: 'capitalize' }}>{statusDisplay}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        
                                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Type" value={ds.scanType} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.projectId} value={ds.projectId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Location" value={ds.location} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Schedule" value={ds.schedule} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={scan.cloudProvider?.toUpperCase()} /></Grid>
                                                <Grid item xs={12}><Divider/></Grid>
                                                <Grid item xs={12} sm={12} md={6}><DetailItem label="Target Table" value={`${ds.targetProjectId}.${ds.targetDatasetId}.${ds.targetTableId}`} /></Grid>
                                                <Grid item xs={12} sm={12} md={6}><DetailItem label="Export Table" value={`${ds.exportProjectId}.${ds.exportDatasetId}.${ds.exportTableId}`} /></Grid>
                                                <Grid item xs={12}><DetailItem label="Description" value={ds.description} /></Grid>
                                            </Grid>
                                        </Box>

                                        {scan.status === 'rejected' && (
                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <DetailItem label="Reason for Rejection" value={scan.reasonForRejection} />
                                             </Box>
                                        )}
                                    </Box>
                                    
                                    {scan?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && (
                                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="contained" size="large" onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                        </Box>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                        <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Data Scan Request</Typography>
                        <IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton>
                    </Box>
                    <Divider />
                    <Typography p={3}>Are you sure you want to approve this Data Scan request?</Typography>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                        <Button type="submit" form="approve-scan-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async ({ reasonForRejection }) => {
                            if (!id) return;
                            try {
                                await queryGraphql(`mutation Reject($input: RejectSolutionInput!) { rejectSolutionDataScans(input: $input) { id } }`, { input: { id, reasonForRejection } });
                                setOpenRejectModal(false);
                                navigate("/datasolutions/requests/data-scans");
                            } catch (error) { console.error("Error during rejection:", error); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="reject-scan-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Data Scan Request</Typography>
                                    <IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 3, flexGrow: 1 }}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                    <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                    <Button type="submit" form="reject-scan-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};

const DataScansApproverPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-data-scans"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <DataScansApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default DataScansApproverPage;